import React, { useMemo } from 'react';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import {
  addBreakdownGroup,
  removeBreakdownGroup,
} from '../../../../redux/slices/oldBuilder';
import BreakdownGroup from './BreakdownGroup';
import {
  FieldContext,
  PermissionKey,
  TagOption,
} from '../../../../utils/apollo/graphqlSchema';
import useQueryTypeFromLocation from '../../../../utils/routes/useQueryTypeFromLocation';
import BuilderSectionCard from '../../BuilderSectionCard';
import { TextButton } from '../../../Button';
import PermissionElementWrapper from '../../../../pages/PermissionControls/PermissionElementWrapper';
import usePermission from '../../../../utils/hooks/permissionHooks/usePermission';
import getBreakdownGroupLimit from '../../../../utils/builder/getBreakdownGroupLimit';
import { assertNotNil } from '../../../../utils/types';
import { activeSchemaIdSelector } from '../../../../redux/selectors/builder';
import { useDndEntityInternalContext } from '../../../DndEntity';
import DropzoneArea from '../../../DndEntity/DndDroppableZone/DropzoneArea';
import {
  EntityDropzoneDataTypeEnum,
  IBreakdownDropzoneProperties,
} from '../../../DndEntity/validation';
import { getDndActiveItemData } from '../../../DndEntity/utils';
import FieldDroppableZone from '../../../DndEntity/DndDroppableZone/FieldDroppableZone';
import { createTagOptionInput } from '../../EntitySelection/utils';
import useQueryBuilderDroppable from '../../../DndEntity/useQueryBuilderDroppable';
import KubitTooltip from '../../../KubitTooltip';

interface IProps {
  enableAddGroups: boolean;
}

const breakdownId = `Entity-Dropzone-Breakdown-Field`;

const tags = [
  createTagOptionInput(TagOption.Field, {
    field: {
      events: [],
      func: null,
      context: FieldContext.Breakdown,
      arrayObjectContext: null,
    },
  }),
];

const BreakdownGroups = ({ enableAddGroups }: IProps) => {
  const dispatch = useAppDispatch();
  const queryType = useQueryTypeFromLocation();
  assertNotNil(queryType);
  const activeSchemaId = useAppSelector(activeSchemaIdSelector);
  const { checkIsPermitted } = usePermission();
  const isPermittedToReadField = checkIsPermitted({
    requiredPermissionKeys: [PermissionKey.FieldRead],
    schemaId: activeSchemaId,
  });
  const { activeItem } = useDndEntityInternalContext();
  const { isBasicBuilderComplete } = useQueryBuilderDroppable();

  const groups = useAppSelector(({ builder }) => builder.breakdown.groups);

  const groupLimit = getBreakdownGroupLimit(queryType);

  const allowAddGroup = enableAddGroups && groups.length < groupLimit;

  const handleAddGroup = () => {
    if (!allowAddGroup) {
      return;
    }

    dispatch(
      addBreakdownGroup({
        groupLimit,
      }),
    );
  };

  const handleRemoveGroup = (groupIndex: number) => () => {
    dispatch(removeBreakdownGroup({ groupIndex }));
  };

  const showAddFieldDropzone = useMemo(() => {
    const data = getDndActiveItemData(activeItem);

    if (
      data &&
      data.__typename === 'TagLeafField' &&
      allowAddGroup &&
      isBasicBuilderComplete
    ) {
      return !groups.some((group) => group.field?.key === data.key);
    }

    return false;
  }, [activeItem, allowAddGroup, groups, isBasicBuilderComplete]);

  return (
    <>
      <PermissionElementWrapper isPermitted={isPermittedToReadField}>
        {showAddFieldDropzone ? (
          <FieldDroppableZone
            dropzoneId={breakdownId}
            tags={tags}
            data={
              {
                id: breakdownId,
                type: EntityDropzoneDataTypeEnum.Breakdowns,
                groupLimit,
                groupIndex: groups.length,
              } satisfies IBreakdownDropzoneProperties
            }
            hasError={!allowAddGroup}
            hideOnDisabled
          >
            <DropzoneArea size="large">
              + Add Field as New Breakdown
            </DropzoneArea>
          </FieldDroppableZone>
        ) : (
          <KubitTooltip
            content={'Select specific properties to group by.'}
            disabled={!allowAddGroup || !isPermittedToReadField}
            placement="bottom"
          >
            <TextButton
              onClick={handleAddGroup}
              disabled={!allowAddGroup || !isPermittedToReadField}
            >
              + Add Breakdown
            </TextButton>
          </KubitTooltip>
        )}
      </PermissionElementWrapper>
      {groups.length > 0 &&
        groups.map((group, index) => (
          <BuilderSectionCard
            key={`breakdown-group-${index}`}
            iconName="breakdown"
            iconPositiveIndex={index + 1}
          >
            <BreakdownGroup
              group={group}
              groupIndex={index}
              onRemove={handleRemoveGroup}
            />
          </BuilderSectionCard>
        ))}
    </>
  );
};

export default BreakdownGroups;
